// Judges a dropped file by the rule the browser applies to `<input accept>`, so
// a drop is filtered the way the OS dialog filters a pick. `accept` is a comma
// list of tokens, compared case-insensitively:
//   `.png`    — the file name ends with it (the only way to match `.p8`/`.jks`,
//               whose MIME type browsers report as empty)
//   `image/*` — the MIME type is in that family
//   `text/csv` — the MIME type is exactly that
// An empty or missing `accept` takes every file, as the dialog does.
//
// The dialog filters `.jpg` by the MIME type it stands for, so it also offers
// `.jpeg` files; a drop is held to the same answer. Each group below is the
// spellings of one type.
const EXTENSION_SPELLINGS: string[][] = [
  [".jpg", ".jpeg"],
  [".tif", ".tiff"]
];

export interface IAcceptPartition {
  accepted: File[];
  rejected: File[];
}

const parseAccept = (accept: string | undefined): string[] => {
  if (accept === undefined) {
    return [];
  }
  return accept
    .split(",")
    .map((token: string): string => token.trim().toLowerCase())
    .filter((token: string): boolean => token.length > 0);
};

const spellingsOf = (extension: string): string[] =>
  EXTENSION_SPELLINGS.find((group: string[]): boolean => group.includes(extension)) ?? [extension];

const matchesToken = (fileName: string, fileType: string, token: string): boolean => {
  if (token.startsWith(".")) {
    return spellingsOf(token).some((extension: string): boolean => fileName.endsWith(extension));
  }
  if (token.endsWith("/*")) {
    return fileType.startsWith(token.slice(0, -1));
  }
  return fileType === token;
};

export const partitionByAccept = (files: File[], accept: string | undefined): IAcceptPartition => {
  const tokens: string[] = parseAccept(accept);
  if (tokens.length === 0) {
    return { accepted: files, rejected: [] };
  }

  const accepted: File[] = [];
  const rejected: File[] = [];
  files.forEach((file: File): void => {
    const fileName: string = file.name.toLowerCase();
    const fileType: string = file.type.toLowerCase();
    const isAccepted: boolean = tokens.some((token: string): boolean => matchesToken(fileName, fileType, token));
    if (isAccepted) {
      accepted.push(file);
    } else {
      rejected.push(file);
    }
  });
  return { accepted, rejected };
};
